import { RoutesList } from 'routes';
import { isArray, isEmpty, isNil, toString } from 'lodash';
// Import Types
import type { DataTableColumn } from 'types';
//Import UI Components
import { Icon } from '@geneui/components';
// Import Components
import LabelTag from 'components/common/LabelTag';
import TittleLink from 'components/common/TittleLink';
import CopyToClipboard from 'components/common/CopyToClipboard';
import CampaignBudgetInfo from 'components/CampaignBudgetInfo';
import ApprovalStateTag from 'components/common/ApprovalStateTag';
// Import Constants
import {
    FilterHolderFields,
    FilterComparisons,
    DynamicInputTypes,
    l,
    CampaignStatusLabels,
    CampaignStatus,
    StateModesLabels,
    FullStateModes,
    DataTableColumnsCustomTypes,
    ArchiveStates,
    TemplateTypesLabels,
    PARTNER_SETTINGS,
    BOOLEAN_DROPDOWN_OPTIONS_FOR_FILTER,
    ApprovalRequestStateOptions,
} from 'constants/common';
// Import Services
import { Helpers } from 'services';
// Import Enums
import { PermissionEnum, SubscriptionSectionEnum } from 'enums';

const { CAMPAIGN_REPORT } = RoutesList;

const { customMomentWithoutTimezoneConversion } = Helpers;

const { Equals, Like, Between, IsBlank, In } = FilterComparisons;
const { textInput, select, rangeDate, multiSelect } = FilterHolderFields;

export interface CampaignListCustomFieldMetadata {
    CustomFieldId: number;
    Name: string;
    Type: number;
}

interface CustomFieldFilterDropdownOption {
    label: string;
    value: number;
}

interface CampaignCustomFieldValueModel {
    CustomFieldId: number;
    FieldValue?: Nullable<string>;
    ViewData?: {
        Name?: Nullable<string>;
        Value?: Nullable<string>;
    };
}

type CampaignCustomFieldsListValueModel = Nullable<CampaignCustomFieldValueModel[] | Record<string, Nullable<string>>>;

export const getCampaignCustomFieldColumnDataKey = (customFieldId: number): string => {
    return `CustomField${customFieldId}`;
};

export const getCampaignCustomFieldFilterValueKey = (customFieldId: number): string => {
    return `CustomField${customFieldId}`;
};

export const getCampaignCustomFieldFilters = (
    customFields: CampaignListCustomFieldMetadata[],
    customFieldOptions: Record<number, CustomFieldFilterDropdownOption[]> = {},
) => {
    return customFields.map(({ CustomFieldId, Name, Type }) => {
        if (Type === DynamicInputTypes.DROP_DOWN) {
            return {
                type: multiSelect.type,
                labelAppearance: 'swap',
                valueFieldKey: getCampaignCustomFieldFilterValueKey(CustomFieldId),
                isMultiSelect: true,
                hasSearch: true,
                comparision: In,
                displayName: Name,
                value: multiSelect.defaultValue,
                data: customFieldOptions[CustomFieldId] ?? [],
            };
        }

        return {
            type: textInput.type,
            labelAppearance: 'swap',
            valueFieldKey: getCampaignCustomFieldFilterValueKey(CustomFieldId),
            comparision: Equals,
            displayName: Name,
            value: textInput.defaultValue,
        };
    });
};

export const getCampaignCustomFieldColumns = (
    customFields: CampaignListCustomFieldMetadata[],
    customFieldNameOverrides: Record<number, string> = {},
): DataTableColumn[] => {
    return customFields.map(({ CustomFieldId, Name }) => ({
        text: customFieldNameOverrides[CustomFieldId] ?? Name,
        dataKey: getCampaignCustomFieldColumnDataKey(CustomFieldId),
        sortable: false,
        isHiddenByDefault: true,
    }));
};

export const mapCampaignCustomFieldsToRowData = (
    customFields: CampaignCustomFieldsListValueModel,
): Record<string, string> => {
    if (isNil(customFields)) {
        return {};
    }

    if (isArray(customFields)) {
        return customFields.reduce<Record<string, string>>((acc, field) => {
            const key = getCampaignCustomFieldColumnDataKey(field.CustomFieldId);
            acc[key] = field?.ViewData?.Value ?? field?.FieldValue ?? '';
            return acc;
        }, {});
    }

    return Object.entries(customFields).reduce<Record<string, string>>((acc, [key, value]) => {
        acc[key] = value ?? '';
        return acc;
    }, {});
};

export const getCampaignCustomFieldNamesFromRowData = (
    customFields: CampaignCustomFieldsListValueModel,
): Record<number, string> => {
    if (!isArray(customFields)) {
        return {};
    }

    return customFields.reduce<Record<number, string>>((acc, field) => {
        if (!isEmpty(field?.ViewData?.Name)) {
            acc[field.CustomFieldId] = field.ViewData?.Name ?? '';
        }

        return acc;
    }, {});
};

export const getCampaignStaticFilterInputsDataList = () => {
    return FilterHolderFieldsConfigs.defaultInputsData.defaultInputsDataList.map((item) => ({
        ...item,
        data: isArray(item.data) ? [...item.data] : item.data,
    }));
};

export const FilterHolderFieldsConfigs = {
    defaultInputsData: {
        defaultInputsDataIds: {
            Channel: 2,
            CategoryId: 5,
        },
        defaultInputsDataList: [
            {
                type: textInput.type,
                labelAppearance: 'swap',
                valueFieldKey: 'CampaignId',
                comparision: Equals,
                displayName: l.CampaignId,
                value: textInput.defaultValue,
            },
            {
                type: textInput.type,
                labelAppearance: 'swap',
                valueFieldKey: 'Name',
                comparision: Like,
                displayName: l.Title,
                value: textInput.defaultValue,
            },
            {
                type: multiSelect.type,
                labelAppearance: 'swap',
                valueFieldKey: 'DeliveryMethod',
                isMultiSelect: true,
                comparision: In,
                displayName: l.Channels,
                value: multiSelect.defaultValue,
                data: [],
            },
            {
                type: textInput.type,
                labelAppearance: 'swap',
                valueFieldKey: 'CreatorName',
                comparision: Like,
                displayName: l.User,
                value: textInput.defaultValue,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'Status',
                comparision: Equals,
                displayName: l.Status,
                value: select.defaultValue,
                data: CampaignStatus,
            },
            {
                type: multiSelect.type,
                labelAppearance: 'swap',
                valueFieldKey: 'CategoryId',
                isMultiSelect: true,
                hasSearch: true,
                comparision: Equals,
                displayName: l.Label,
                value: multiSelect.defaultValue,
                data: [],
            },
            {
                type: multiSelect.type,
                labelAppearance: 'swap',
                valueFieldKey: 'ApprovalRequest.State',
                isMultiSelect: true,
                comparision: In,
                displayName: l.ApprovalState,
                value: multiSelect.defaultValue,
                data: ApprovalRequestStateOptions,
                partnerSettings: PARTNER_SETTINGS.IsApprovalRequired,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'IsNonMarketing',
                comparision: Equals,
                displayName: l.NonMarketing,
                value: select.defaultValue,
                data: BOOLEAN_DROPDOWN_OPTIONS_FOR_FILTER,
                permissions: PermissionEnum.ManageNonMarketingCampaign,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'IsPublic',
                comparision: Equals,
                displayName: l.Public,
                value: select.defaultValue,
                data: BOOLEAN_DROPDOWN_OPTIONS_FOR_FILTER,
                partnerSettings: PARTNER_SETTINGS.IsAllowABTest,
            },
            {
                type: rangeDate.type,
                labelAppearance: 'swap',
                valueFieldKey: 'LastRunDate',
                comparision: Between,
                popoverAlign: 'start',
                displayName: l.LastRunDate,
                value: rangeDate.defaultValue,
            },
            {
                type: rangeDate.type,
                labelAppearance: 'swap',
                valueFieldKey: 'CreatedDate',
                comparision: Between,
                popoverAlign: 'start',
                displayName: l.CreatedDate,
                value: rangeDate.defaultValue,
            },
            {
                type: rangeDate.type,
                labelAppearance: 'swap',
                valueFieldKey: 'NextRunDate',
                comparision: Between,
                popoverAlign: 'start',
                displayName: l.NextRunDate,
                value: rangeDate.defaultValue,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'State',
                comparision: Equals,
                displayName: l.Mode,
                value: select.defaultValue,
                data: FullStateModes,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'ArchivedDate',
                comparision: IsBlank,
                displayName: l.Archive,
                value: select.defaultValue,
                data: ArchiveStates,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'HasBonus',
                comparision: Equals,
                displayName: l.Award,
                value: select.defaultValue,
                data: BOOLEAN_DROPDOWN_OPTIONS_FOR_FILTER,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'HasBudget',
                comparision: Equals,
                displayName: l.Budget,
                value: select.defaultValue,
                data: BOOLEAN_DROPDOWN_OPTIONS_FOR_FILTER,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'HasABTest',
                comparision: Equals,
                displayName: l.ABTest,
                value: select.defaultValue,
                data: BOOLEAN_DROPDOWN_OPTIONS_FOR_FILTER,
            },
        ],
    },
};

const addGoalBlocker = (status) => {
    return +status === +CampaignStatusLabels.Completed || +status === +CampaignStatusLabels.Stopped;
};

export const getTitle = (row) => {
    return row.data.Name.Name;
};

export const dataTableColumnsConfigs = (
    t,
    dateFormats,
    customFieldColumns: DataTableColumn[] = [],
): { columns: DataTableColumn[] } => {
    const { partnerDateTimeFormat } = dateFormats;
    return {
        columns: [
            {
                text: t(l.CampaignId),
                dataKey: 'CampaignId',
                sortable: true,
                colRenderer: (id) => <CopyToClipboard>{id}</CopyToClipboard>,
            },
            {
                text: t(l.ApprovalState),
                dataKey: 'ApprovalRequest.State',
                sortable: false,
                partnerSettings: [PARTNER_SETTINGS.IsApprovalRequired],
                colRenderer: (_state, _index, row) => {
                    const { ApprovalRequest } = row?.data ?? {};
                    return isNil(ApprovalRequest) ? null : (
                        <ApprovalStateTag approvalRequest={ApprovalRequest} isLink={true} />
                    );
                },
            },
            {
                text: t(l.Title),
                dataKey: 'Name',
                sortable: true,
                isPermanent: true,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                colRenderer: ({ Name, CampaignId, HasBonus }: any, _i, row) => {
                    const { ABTests } = row.data;
                    return (
                        <div className="crm-list-column-title-icon-components">
                            <TittleLink path={CAMPAIGN_REPORT} name={Name} queryParams={{ campaignId: CampaignId }} />
                            {ABTests?.length > 0 ? (
                                <Icon type="bc-icon-test-48" />
                            ) : HasBonus ? (
                                <Icon type="bc-icon-send-bonus-block" />
                            ) : null}
                        </div>
                    );
                },
            },
            {
                text: t(l.Channels),
                dataKey: 'Channels',
                sortable: false,
                colRenderer: (DeliveryMethod, _index, row) => {
                    const { MessageProvider } = row?.data ?? {};
                    return isNil(DeliveryMethod) ? (
                        <></>
                    ) : (
                        <TittleLink
                            isClickable={false}
                            name={`${t(l[TemplateTypesLabels[DeliveryMethod]])} ${
                                isEmpty(MessageProvider?.Name) || DeliveryMethod === TemplateTypesLabels.Internal
                                    ? ''
                                    : `(${MessageProvider?.Name})`
                            }`}
                        />
                    );
                },
            },
            {
                text: t(l.User),
                dataKey: 'CreatedBy.Name',
                sortable: true,
            },
            {
                text: t(l.Status),
                dataKey: 'Status',
                sortable: true,
                colRenderer: (status) => (
                    <div className={`crm-status-badge-cnt crm-cj-status-label-${status}`}>
                        {t(CampaignStatusLabels[status])}
                    </div>
                ),
            },
            {
                text: t(l.Budget),
                dataKey: 'Budget',
                sortable: true,
                colRenderer: (_budget, _index, row) =>
                    !isNil(row.data.CampaignLimit) ? <CampaignBudgetInfo {...row.data.CampaignLimit} /> : t(l.NoLimit),
                subscriptionSection: SubscriptionSectionEnum.PromoBudget,
            },
            {
                type: DataTableColumnsCustomTypes.Date,
                text: t(l.CreatedDate),
                dataKey: 'CreatedDate',
                sortable: true,
                formatter: (CreatedDate) => {
                    return (
                        CreatedDate && customMomentWithoutTimezoneConversion(CreatedDate).format(partnerDateTimeFormat)
                    );
                },
            },
            {
                type: DataTableColumnsCustomTypes.Date,
                text: t(l.LastRunDate),
                dataKey: 'LastRunDate',
                sortable: true,
                formatter: (LastRunDate) => {
                    return (
                        LastRunDate && customMomentWithoutTimezoneConversion(LastRunDate).format(partnerDateTimeFormat)
                    );
                },
            },
            {
                type: DataTableColumnsCustomTypes.Date,
                text: t(l.NextRunDate),
                dataKey: 'NextRunDate',
                sortable: true,
                formatter: (NextRunDate) => {
                    return (
                        NextRunDate && customMomentWithoutTimezoneConversion(NextRunDate).format(partnerDateTimeFormat)
                    );
                },
            },
            {
                text: t(l.NonMarketing),
                dataKey: 'IsNonMarketing',
                sortable: true,
                colRenderer: (IsNonMarketing) => (IsNonMarketing ? t(l.Yes) : t(l.No)),
                permissions: [PermissionEnum.ManageNonMarketingCampaign],
            },
            {
                text: t(l.Public),
                dataKey: 'IsPublic',
                sortable: true,
                colRenderer: (IsPublic) => (IsPublic ? t(l.Yes) : t(l.No)),
                partnerSettings: [PARTNER_SETTINGS.IsAllowABTest],
            },
            {
                text: t(l.Goal),
                dataKey: 'GoalName',
                sortable: false,
                colRenderer: (_name, _index, row) => (
                    <div className="set-category">
                        {isNil(row?.data?.GoalName) ? (
                            <>{t(l.NA)}</>
                        ) : (
                            <LabelTag
                                color={`var(${`--status-label-goal-color`})`}
                                labelName={row?.data?.GoalName}
                                onClick={() => {
                                    !addGoalBlocker(row.data.Status) && row.addGoalFn(row?.data);
                                }}
                                icon={
                                    !addGoalBlocker(row.data.Status) ? (
                                        <button className="remove-btn" onClick={() => row.deleteGoalFn(row?.data)}>
                                            <Icon type="bc-icon-is-not" />
                                        </button>
                                    ) : (
                                        <></>
                                    )
                                }
                            />
                        )}
                    </div>
                ),
            },
            {
                type: DataTableColumnsCustomTypes.Date,
                text: t(l.LastModified),
                dataKey: 'ModifiedDate',
                sortable: true,
                formatter: (ModifiedDate) => {
                    return (
                        ModifiedDate &&
                        customMomentWithoutTimezoneConversion(ModifiedDate).format(partnerDateTimeFormat)
                    );
                },
            },
            {
                text: t(l.Mode),
                dataKey: 'State',
                sortable: true,
                colRenderer: (state) => (
                    <div className={`crm-mode-badge-cnt crm-cj-mode-label-${state}`}>{t(StateModesLabels[state])}</div>
                ),
            },
            ...customFieldColumns,
        ],
    };
};

const isAllow = (campaign) => {
    const { ABTests, Status, ArchivedDate, IsPublic, IsNonMarketing } = campaign;
    const status = toString(Status);

    const isArchive = !isNil(ArchivedDate);
    const isCompleted = status === CampaignStatusLabels.Completed;
    const isStopped = status === CampaignStatusLabels.Stopped;
    const isInProgress = status === CampaignStatusLabels.InProgress;
    const isIdle = status === CampaignStatusLabels.Idle;

    const isNotExistsABTest = isNil(ABTests) || ABTests.length === 0;

    return {
        delete: !isArchive && !isCompleted && !isStopped && !isInProgress && !isIdle,
        archive: (!isArchive && isCompleted) || (!isArchive && isStopped),
        unarchive: isArchive,
        makePublic: isNotExistsABTest && !IsPublic && !IsNonMarketing,
        makePrivate: isNotExistsABTest && IsPublic && !IsNonMarketing,
    };
};

export const getCampaignsNamesIds = (selectedRows) => {
    if (!isArray(selectedRows)) return;

    type ListData = {
        campaignId: string;
        name: string;
    };

    const allowDeleteCampaigns: ListData[] = [];
    const notAllowDeleteCampaigns: ListData[] = [];
    const allowArchiveCampaigns: ListData[] = [];
    const notAllowArchiveCampaigns: ListData[] = [];
    const allowUnarchiveCampaigns: ListData[] = [];
    const notAllowUnarchiveCampaigns: ListData[] = [];
    const allowMakePublicCampaigns: ListData[] = [];
    const notAllowMakePublicCampaigns: ListData[] = [];
    const allowMakePrivateCampaigns: ListData[] = [];
    const notAllowMakePrivateCampaigns: ListData[] = [];

    selectedRows.forEach(({ data }) => {
        const campaigns: ListData = { campaignId: data.CampaignId, name: data.Name.Name };
        const isAllowData = isAllow(data);

        if (isAllowData.delete) {
            allowDeleteCampaigns.push(campaigns);
        } else {
            notAllowDeleteCampaigns.push(campaigns);
        }

        if (isAllowData.archive) {
            allowArchiveCampaigns.push(campaigns);
        } else {
            notAllowArchiveCampaigns.push(campaigns);
        }

        if (isAllowData.unarchive) {
            allowUnarchiveCampaigns.push(campaigns);
        } else {
            notAllowUnarchiveCampaigns.push(campaigns);
        }

        if (isAllowData.makePublic) {
            allowMakePublicCampaigns.push(campaigns);
        } else {
            notAllowMakePublicCampaigns.push(campaigns);
        }

        if (isAllowData.makePrivate) {
            allowMakePrivateCampaigns.push(campaigns);
        } else {
            notAllowMakePrivateCampaigns.push(campaigns);
        }
    });

    return {
        allowDeleteCampaigns,
        notAllowDeleteCampaigns,
        allowArchiveCampaigns,
        notAllowArchiveCampaigns,
        allowUnarchiveCampaigns,
        notAllowUnarchiveCampaigns,
        allowMakePublicCampaigns,
        notAllowMakePublicCampaigns,
        allowMakePrivateCampaigns,
        notAllowMakePrivateCampaigns,
    };
};

export const modalsStateSetDictKeys = {
    deleteCampaign: 'deleteCampaign',
    archiveCampaign: 'archiveCampaign',
    unarchiveCampaign: 'unarchiveCampaign',
    stopSendCampaign: 'stopSendCampaign',
    infoCampaign: 'infoCampaign',
    setGoalCampaign: 'setGoalCampaign',
    deleteGoalCampaign: 'deleteGoalCampaign',
    deleteTemplate: 'deleteTemplate',
    testTemplate: 'testTemplate',
    archiveTemplate: 'archiveTemplate',
    unarchiveTemplate: 'unarchiveTemplate',
    editTemplate: 'editTemplate',
    deleteSegment: 'deleteSegment',
    convertToReport: 'convertToReport',
    archiveSegment: 'archiveSegment',
    unarchiveSegment: 'unarchiveSegment',
    editSegment: 'editSegment',
    stopABTest: 'stopABTest',
    makePublicCampaign: 'makePublicCampaign',
    makePrivateCampaign: 'makePrivateCampaign',
};
